import React, { KeyboardEvent, useState } from "react";

import { useIntl, FormattedMessage } from "react-intl";

import LoadingButton from "@atlaskit/button/loading-button";
import { FormFooter } from "@atlaskit/form";
import { ErrorMessage } from "@atlaskit/form";
import Heading from "@atlaskit/heading";
import { Flex, Stack, Box, xcss, media } from "@atlaskit/primitives";

import { PageWorkTypesLeftProps } from "../../../common/types";
import { useIsTeamTypeSpaceRecommendationExperiment } from "../../../hooks/experiment-hooks";
import { IssueTypeIcon } from "../../assets/issue-type-svgs";

import messages from "./messages";

export const LeftHandSidePageWorkTypes: React.FC<PageWorkTypesLeftProps> = ({
    defaultIssueTypes,
    selectedIssueTypes,
    onToggleIssueType,
    onClickNext
}) => {
    const { formatMessage } = useIntl();
    const [showError, setShowError] = useState(false);
    const isTeamTypeSpaceRecommendationExperiment: boolean =
        useIsTeamTypeSpaceRecommendationExperiment();

    const handleNextClick = () => {
        if (selectedIssueTypes.length === 0) {
            setShowError(true);
        } else {
            setShowError(false);
            onClickNext();
        }
    };

    return (
        <Flex
            direction="column"
            justifyContent="space-between"
            xcss={containerStyles}
        >
            <Stack space="space.150">
                <Heading as="h1" size="xlarge">
                    <FormattedMessage {...messages.chooseWorkTypes} />
                </Heading>
                <Box xcss={subtextStyles}>
                    <FormattedMessage
                        {...(!isTeamTypeSpaceRecommendationExperiment
                            ? messages.workTypesDescription
                            : messages.workTypesDescriptionVariation)}
                    />
                </Box>
                <Box xcss={scrollableContainerStyles} role="list">
                    <Stack space="space.050">
                        {defaultIssueTypes.map((option) => (
                            <Box
                                xcss={[
                                    pressStyles,
                                    selectedIssueTypes.includes(option)
                                        ? activePressStyles
                                        : undefined
                                ]}
                                testId={option.id}
                                key={option.id}
                                onClick={() => {
                                    onToggleIssueType(option);
                                    setShowError(false);
                                }}
                                onKeyDown={(e: KeyboardEvent) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        onToggleIssueType(option);
                                        setShowError(false);
                                    }
                                }}
                                role="listitem"
                                tabIndex={0}
                            >
                                <Flex alignItems="center" columnGap="space.300">
                                    <Flex
                                        alignItems="center"
                                        justifyContent="center"
                                        xcss={iconStyles}
                                    >
                                        <IssueTypeIcon type={option.icon} />
                                    </Flex>
                                    <Flex direction="column" alignItems="start">
                                        <Box
                                            as="span"
                                            id={`option-name-${option.name}`}
                                        >
                                            {formatMessage(option.name)}
                                        </Box>
                                        <Box
                                            xcss={descriptionStyles}
                                            id={`option-description-${option.name}`}
                                        >
                                            {formatMessage(option.description)}
                                        </Box>
                                    </Flex>
                                </Flex>
                            </Box>
                        ))}
                    </Stack>
                </Box>
                {showError && (
                    <ErrorMessage>
                        <FormattedMessage {...messages.issueTypeValidation} />
                    </ErrorMessage>
                )}
                <Box as="p" xcss={subtextStyles}>
                    <FormattedMessage {...messages.addMoreWorkTypesLater} />
                </Box>
            </Stack>
            <FormFooter>
                <Flex
                    justifyContent="end"
                    alignItems="center"
                    xcss={footerStyles}
                >
                    <LoadingButton
                        type="submit"
                        appearance="primary"
                        isLoading={false}
                        onClick={handleNextClick}
                        aria-labelledby="next-button step-label"
                    >
                        <FormattedMessage {...messages.nextButtonCta} />
                    </LoadingButton>
                </Flex>
            </FormFooter>
        </Flex>
    );
};

const containerStyles = xcss({
    height: "100%"
});

const scrollableContainerStyles = xcss({
    [media.above.sm]: {
        maxHeight: "360px",
        overflowY: "auto"
    }
});

const pressStyles = xcss({
    "border": "2px solid",
    "borderRadius": "border.radius.200",
    "borderColor": "color.border",
    "backgroundColor": "elevation.surface",
    "maxWidth": "420px",
    "minHeight": "64px",
    "padding": "space.050",
    ":hover": {
        borderColor: "color.border.focused"
    }
});

const subtextStyles = xcss({
    fontSize: "font.body.small",
    color: "color.text.subtle",
    margin: "space.0"
});

const activePressStyles = xcss({
    borderColor: "color.border.focused",
    borderWidth: "border.width.indicator"
});

const iconStyles = xcss({
    paddingInlineStart: "space.150"
});

const descriptionStyles = xcss({
    fontSize: "font.body.small",
    color: "color.text.subtle"
});

const footerStyles = xcss({
    width: "100%",
    color: "color.text.subtlest"
});
